import ReferralApi from '_services/Api/Referral';
import { getOrCreateAnonId } from '_utils/abTestingUtils';
import ReferralContextService from './ReferralContextService';

type ReferralTouchpoint = 'app-entry' | 'onboarding-offer';

const REFERRAL_TOUCH_PREFIX = 'referral_touch';
const REFERRAL_TOUCHED_PREFIX = 'referral_touched';
const REFERRAL_CLAIM_PREFIX = 'referral_claimed';

const inFlight = new Set<string>();

const getLocalStorage = (): Storage | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        return window.localStorage;
    } catch {
        return null;
    }
};

const isStored = (storage: Storage | null, key: string): boolean => {
    return storage?.getItem(key) === '1';
};

const storeFlag = (storage: Storage | null, key: string): void => {
    storage?.setItem(key, '1');
};

const hasTouchedReferral = (
    storage: Storage | null,
    anon_id: string,
): boolean => {
    return isStored(storage, `${REFERRAL_TOUCHED_PREFIX}:${anon_id}`);
};

const runOnce = async (key: string, task: () => Promise<void>) => {
    const storage = getLocalStorage();
    if (inFlight.has(key) || isStored(storage, key)) {
        return;
    }

    inFlight.add(key);
    try {
        await task();
        storeFlag(storage, key);
    } finally {
        inFlight.delete(key);
    }
};

const touchReferral = async (touchpoint: ReferralTouchpoint): Promise<void> => {
    const referral_uuid = ReferralContextService.getUuid();
    if (!referral_uuid) {
        return;
    }

    const anon_id = getOrCreateAnonId();
    const key = `${REFERRAL_TOUCH_PREFIX}:${touchpoint}:${referral_uuid}:${anon_id}`;

    try {
        await runOnce(key, async () => {
            const response = await ReferralApi.onboard({
                referral_uuid,
                anon_id,
            });
            if (!response.data.ok) {
                throw new Error('Referral onboard was not accepted');
            }
        });
        storeFlag(getLocalStorage(), `${REFERRAL_TOUCHED_PREFIX}:${anon_id}`);
    } catch (error) {
        console.error(`Referral touch failed: ${touchpoint}`, error);
    }
};

const claimReferralForUser = async (user_id: number): Promise<void> => {
    if (!Number.isFinite(user_id)) {
        return;
    }

    const anon_id = getOrCreateAnonId();
    const storage = getLocalStorage();
    if (!hasTouchedReferral(storage, anon_id)) {
        return;
    }

    const key = `${REFERRAL_CLAIM_PREFIX}:${anon_id}:${user_id}`;

    try {
        await runOnce(key, async () => {
            const response = await ReferralApi.claim({ anon_id });
            if (!response.data.ok) {
                throw new Error('Referral claim was not accepted');
            }
        });
    } catch (error) {
        console.error('Referral claim failed', error);
    }
};

const ReferralAttributionService = {
    trackAppEntry: (): Promise<void> => touchReferral('app-entry'),
    trackOnboardingOfferShown: (): Promise<void> =>
        touchReferral('onboarding-offer'),
    claimForLoggedInUser: (user_id: number): Promise<void> =>
        claimReferralForUser(user_id),
};

export default ReferralAttributionService;
